import * as aq from "arquero";
import {DPTableAddSumColumnSettings, DPTableAddSumRowSettings} from "@dp-types/DPTableJSLibraryTypes";
import {DazzlerMethodAbstract} from "../../../decorators/dazzler/DazzlerMethodAbstract";
import {DazzlerMethodGroup, DazzlerMethodGroupName} from "../../../decorators/dazzler/DazzlerMethodGroup";
import {DazzlerMethodParameter} from "../../../decorators/dazzler/DazzlerMethodParameter";

export default class DPTableJSLibrary {

  static NAMESPACE: string = "DPTable";
  static SYSTEM_COLS: string[] = ["id", "path"];

  static functions(globals: any): any[] {
    return [
      {
        name: "addTotalRow",
        fn: DPTableJSLibrary.addTotalRow.bind(null, globals.$.data)
      },
      {
        name: "addTotalColumn",
        fn: DPTableJSLibrary.addTotalColumn.bind(null, globals.$.data)
      },
      {
        name: "aq",
        fn: aq
      }
    ].map(el => ({...el, namespace: DPTableJSLibrary.NAMESPACE}));
  }

  @DazzlerMethodAbstract("Adds a sum row to the bottom of the table", {returns: "any"})
  @DazzlerMethodParameter("settings", {description: "The settings to add the sum row to, including custom formulas for each column", type: "object"})
  @DazzlerMethodGroup(DazzlerMethodGroupName.GENERAL)
  static addTotalRow(data: Record<string, unknown>[], settings: DPTableAddSumRowSettings = {}) {
    if (!data.length) { return data; }
    const aqTable = aq.from(data);

    const columnNames = aqTable.columnNames();
    const firstNonSystemCol = columnNames.find(col => !DPTableJSLibrary.SYSTEM_COLS.includes(col));
    const compareToFirstNonSystemCol = columnNames.find(col => col.includes(`compare_to_value::${firstNonSystemCol}`));

    const numericCols = columnNames.filter(col =>
      aqTable.array(col).every((val: unknown) => val == null || typeof val === "number")
    );

    const sumRow = aqTable.rollup(
      Object.fromEntries(
        numericCols.map(col => [col, settings.customSumFormula?.[col]?.aggregationFormula ?? aq.op.sum(col)])
      )
    ).object() as Record<string, unknown>;
  
    // Apply post-aggregation formulas
    if (settings.customSumFormula) {
      for (const [col, formula] of Object.entries(settings.customSumFormula)) {
        try {
          sumRow[col] = formula?.postAggregationFormula?.(sumRow, aqTable) ?? sumRow[col];
        } catch (e) {
          logger.error(`Error applying post-aggregation formula for column ${col}: ${e}`);
        }
      }
    }

    if (firstNonSystemCol && !numericCols.includes(firstNonSystemCol)) {
      sumRow[firstNonSystemCol] = "Total";
    }

    if (compareToFirstNonSystemCol && !numericCols.includes(compareToFirstNonSystemCol)) {
      sumRow[compareToFirstNonSystemCol] = "Total";
    }

    sumRow.dp_id = "__dp_total_row__";
    data.push(sumRow);

  }

  @DazzlerMethodAbstract("Adds a sum column to the table", {returns: "any"})
  @DazzlerMethodParameter("settings", {description: "The settings to add the sum row to", type: "object"})
  @DazzlerMethodGroup(DazzlerMethodGroupName.GENERAL)
  static addTotalColumn(data: Record<string, unknown>[], settings: DPTableAddSumColumnSettings = {}) {
    if (!data.length) return data;
  
    const aqTable = aq.from(data);
    const columnNames = aqTable.columnNames();
  
    const numericCols = columnNames.filter(col =>
      aqTable.array(col).every((val: unknown) => (val == null || typeof val === "number") && !col.includes("compare_to_value"))
    );

    const numericCompareToCols = columnNames.filter(col => 
      aqTable.array(col).every((val: unknown) => (val == null || typeof val === "number") && col.includes("compare_to_value"))
    );
  
    const sumColName = "__dp_total_col__";
    const compareToSumColName = "compare_to_value::__dp_total_col__";

    const defaultFormula = (d: Record<string, any>) => numericCols.reduce((acc, col) => acc + (d[col] ?? 0), 0);
  
    const withSum = aqTable.derive({
      [sumColName]: aq.escape((d: Record<string, any>, index: number) => 
        settings.customFormulaPerRow?.[index](d) ?? defaultFormula(d))
    });

    for (let i = 0; i < withSum.objects().length; i++) {
      data[i][sumColName] = (withSum.objects() as Record<string, unknown>[])[i][sumColName];
    }

    if (numericCompareToCols.length) {
      const withCompareToSum = withSum.derive({
        [compareToSumColName]: aq.escape((d: Record<string, any>, index: number) => 
          numericCompareToCols.reduce((acc, col) => acc + (d[col] ?? 0), 0))
      });
  
      for (let i = 0; i < withCompareToSum.objects().length; i++) {
        data[i][compareToSumColName] = (withCompareToSum.objects() as Record<string, unknown>[])[i][compareToSumColName];
      }
    }

  }

}